/**
 * The creators a running search has saved so far, as compact rows in a
 * short scroll box — it sits under the progress line rather than taking over
 * the screen. Once the search is done, rows can be ticked to remove.
 *
 * TODO(discovery-rebuild): switch to the shared LeadTable (agent A) once it
 * lands; this list is deliberately small and self-contained until then.
 */
import { useMemo, type JSX } from 'react';
import { PlatformIcon } from '@/components/PlatformIcon';
import { RowCheckbox, SelectAllCheckbox, selectableRowClass } from '@/components/ui/range-select';
import { useRangeSelection } from '@/hooks/useRangeSelection';
import { cn } from '@/lib/utils';
import { AudienceBadge } from '../components/AudienceBadge';
import { PageTag } from '../components/PageTag';
import { engagement, engagementTooltip, formatEngagement } from '../lib/engagement';
import { leadKey, profileUrl, toPlatform } from '../lib/platform';
import type { LeadEnrichment } from '../api/types';
import { compactNumber } from './params';

export function liveLeadKey(lead: LeadEnrichment): string {
  return leadKey(lead.platform as string | undefined, lead.username ?? '');
}

const num = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? compactNumber(n) : '—');

export function LiveResults({
  leads,
  selectable,
  selected,
  onApply,
}: {
  leads: LeadEnrichment[];
  /** Ticking to remove is offered once the search is done. */
  selectable: boolean;
  selected: ReadonlySet<string>;
  /** Set every key in `keys` to `checked` (one row, a shift-click range, or all). */
  onApply: (keys: string[], checked: boolean) => void;
}): JSX.Element | null {
  // A creator can be saved twice in one run; the first row stands for both.
  const keys = useMemo(() => [...new Set(leads.map(liveLeadKey))], [leads]);
  const range = useRangeSelection({ visible: selectable ? keys : [], isSelected: (k) => selected.has(k), onApply });
  if (leads.length === 0) return null;
  return (
    <div className="dx-scroll-pane max-h-72 overflow-y-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card">
          <tr className="text-left text-xs font-semibold text-muted-foreground/80">
            {selectable && (
              <th className="w-8 px-3 py-1.5">
                <SelectAllCheckbox
                  label="Tick every creator to remove"
                  state={range.headState}
                  onToggle={range.toggleAll}
                  className="rounded border-input accent-[var(--destructive)]"
                />
              </th>
            )}
            <th className="px-3 py-1.5 font-semibold">Creator</th>
            <th className="px-3 py-1.5 text-right font-semibold">Followers</th>
            <th className="px-3 py-1.5 text-right font-semibold">Avg views</th>
            <th className="hidden px-3 py-1.5 text-right font-semibold sm:table-cell">Engagement</th>
            <th className="hidden px-3 py-1.5 font-semibold md:table-cell">Email</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead, i) => {
            const key = liveLeadKey(lead);
            const isSelected = selected.has(key);
            const eng = engagement(lead);
            const username = lead.username ?? '';
            return (
              <tr
                key={`${key}-${i}`}
                className={cn(
                  'border-t border-border transition-colors',
                  isSelected ? 'bg-destructive/10' : 'hover:bg-accent/40',
                  selectable && selectableRowClass(),
                )}
                {...(selectable ? range.rowProps(key) : {})}
              >
                {selectable && (
                  <td className="w-8 px-3 py-1.5">
                    <RowCheckbox
                      aria-label={`Remove @${username}`}
                      className="rounded border-input accent-[var(--destructive)]"
                      {...range.checkboxProps(key)}
                    />
                  </td>
                )}
                <td className="max-w-0 px-3 py-1.5">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <PlatformIcon platform={toPlatform(lead.platform)} size={14} className="shrink-0 text-muted-foreground" />
                    <a
                      href={profileUrl(lead.platform as string | undefined, username)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate font-medium hover:underline"
                    >
                      @{username}
                    </a>
                    {lead.nickname && <span className="hidden truncate text-xs text-muted-foreground lg:inline">{String(lead.nickname)}</span>}
                    <AudienceBadge className="shrink-0" region={lead.region} audience={lead.audienceGeo} />
                    <span className="shrink-0">
                      <PageTag enrichment={lead} />
                    </span>
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{num(lead.followers)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{num(lead.avgViews)}</td>
                <td className="hidden px-3 py-1.5 text-right tabular-nums text-muted-foreground sm:table-cell">
                  <span title={engagementTooltip(eng)}>{formatEngagement(eng)}</span>
                </td>
                <td className="hidden max-w-[14rem] truncate px-3 py-1.5 text-xs text-muted-foreground md:table-cell">
                  {lead.email ? String(lead.email) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
